<script setup lang="ts">
/**
 * MY 클라이언트 Contact 주소 Consolidate(병합) 다이얼로그.
 * 3단계: ① 2개 선택 + keep 지정 ② Review(재지정 계획) ③ Confirm(고아 주소 soft-delete 옵션 + 실행).
 * 병합 = merge 주소의 역할 매핑을 keep 으로 재지정(L_CONTACT_BRANCH_MAP UPDATE).
 * 삭제 체크 시 merge 주소 행 IS_DEL=1(soft delete).
 */
import { ref, computed, watch, onMounted } from 'vue';
import { contactsApi } from '../api/contactsApi';
import { useDialog } from '@/shared/composables/useDialog';
import { useTranslation } from '@/utils/locales/messages';

const { createSyncT } = useTranslation();
const currentLanguage = ref(localStorage.getItem('preferred-language') || 'en');
const t = createSyncT(currentLanguage);
onMounted(() => {
    window.addEventListener('language-changed', (event: any) => {
        currentLanguage.value = event.detail.language;
    });
});
/** t + 영문 fallback 헬퍼 */
function tt(key: string, fallback: string): string {
    return t(`contact.${key}`) || fallback;
}

interface AddressItem {
    id: number;
    addressType: string;
    addressTypeLabel: string;
    alias: string;
    displayLine: string;
    roleCount: number;
}
interface ReassignRow {
    entityCd: string;
    companyName: string;
    positionCd: string;
    position: string;
    addrType: string;
    addrTypeLabel: string;
    action?: 'reassign' | 'drop';
}

const props = defineProps<{ modelValue: boolean; contactCd: string }>();
const emit = defineEmits<{ 'update:modelValue': [boolean]; consolidated: [] }>();

const { confirm, alert } = useDialog();

const dialog = computed({
    get: () => props.modelValue,
    set: (v: boolean) => emit('update:modelValue', v),
});

const step = ref<1 | 2 | 3>(1);
const isLoadingList = ref(false);
const isLoadingPreview = ref(false);
const isSubmitting = ref(false);

const addresses = ref<AddressItem[]>([]);
const selectedIds = ref<number[]>([]);
const keepId = ref<number | null>(null);
const deleteOrphan = ref(false);

const keepMaps = ref<ReassignRow[]>([]);
const reassignments = ref<ReassignRow[]>([]);
const collisionCount = ref(0);
const reassignCount = ref(0);

const mergeId = computed<number | null>(() => {
    if (selectedIds.value.length !== 2 || keepId.value == null) return null;
    return selectedIds.value.find((id) => id !== keepId.value) ?? null;
});
const keepAddress = computed(() => addresses.value.find((a) => a.id === keepId.value) || null);
const mergeAddress = computed(() => addresses.value.find((a) => a.id === mergeId.value) || null);
const canReview = computed(() => selectedIds.value.length === 2 && keepId.value != null && mergeId.value != null);

function resetState() {
    step.value = 1;
    addresses.value = [];
    selectedIds.value = [];
    keepId.value = null;
    deleteOrphan.value = false;
    keepMaps.value = [];
    reassignments.value = [];
    collisionCount.value = 0;
    reassignCount.value = 0;
}

function closeDialog() {
    dialog.value = false;
}

async function loadList() {
    const cd = String(props.contactCd || '').trim();
    if (!cd) return;
    isLoadingList.value = true;
    try {
        const resp = (await contactsApi.getAddressConsolidateList(cd)) as any;
        addresses.value = Array.isArray(resp?.addresses) ? resp.addresses : [];
    } catch (e: unknown) {
        addresses.value = [];
        await alert({
            title: tt('Error', 'Error'),
            message: e instanceof Error ? e.message : tt('Failed to load addresses.', 'Failed to load addresses.'),
            type: 'error',
        });
    } finally {
        isLoadingList.value = false;
    }
}

/** 선택 토글 — 최대 2개 */
function toggleSelect(id: number) {
    const idx = selectedIds.value.indexOf(id);
    if (idx >= 0) {
        selectedIds.value.splice(idx, 1);
        if (keepId.value === id) keepId.value = null;
        return;
    }
    if (selectedIds.value.length >= 2) return; // 2개 초과 방지
    selectedIds.value.push(id);
    // keep 미지정이면 첫 선택을 기본 keep 으로
    if (keepId.value == null) keepId.value = id;
}

function isSelected(id: number) {
    return selectedIds.value.includes(id);
}

async function goToReview() {
    if (!canReview.value || keepId.value == null || mergeId.value == null) return;
    isLoadingPreview.value = true;
    try {
        const resp = (await contactsApi.getAddressConsolidatePreview(
            String(props.contactCd),
            keepId.value,
            mergeId.value,
        )) as any;
        keepMaps.value = Array.isArray(resp?.keepMaps) ? resp.keepMaps : [];
        reassignments.value = Array.isArray(resp?.reassignments) ? resp.reassignments : [];
        collisionCount.value = Number(resp?.collisionCount ?? 0) || 0;
        reassignCount.value = Number(resp?.reassignCount ?? 0) || 0;
        step.value = 2;
    } catch (e: unknown) {
        await alert({
            title: tt('Error', 'Error'),
            message: e instanceof Error ? e.message : tt('Failed to load review.', 'Failed to load review.'),
            type: 'error',
        });
    } finally {
        isLoadingPreview.value = false;
    }
}

function goToConfirm() {
    step.value = 3;
}

async function executeConsolidate() {
    if (keepId.value == null || mergeId.value == null || isSubmitting.value) return;
    const ok = await confirm({
        title: tt('Confirm', 'Confirm'),
        message: deleteOrphan.value
            ? tt('Move the role mappings to the kept address and delete the merged address?',
                'Move the role mappings to the kept address and delete the merged address?')
            : tt('Move the role mappings to the kept address?',
                'Move the role mappings to the kept address?'),
        type: 'warning',
    });
    if (!ok) return;

    isSubmitting.value = true;
    try {
        await contactsApi.commitAddressConsolidate(String(props.contactCd), {
            keepId: keepId.value,
            mergeId: mergeId.value,
            deleteOrphan: deleteOrphan.value,
        });
        await alert({
            title: tt('Success', 'Success'),
            message: tt('Addresses consolidated successfully.', 'Addresses consolidated successfully.'),
            type: 'success',
        });
        emit('consolidated');
        closeDialog();
    } catch (e: unknown) {
        await alert({
            title: tt('Error', 'Error'),
            message: e instanceof Error ? e.message : tt('Failed to consolidate addresses.', 'Failed to consolidate addresses.'),
            type: 'error',
        });
    } finally {
        isSubmitting.value = false;
    }
}

watch(
    () => props.modelValue,
    (open) => {
        if (open) {
            resetState();
            void loadList();
        }
    },
    { immediate: true },
);
</script>

<template>
    <v-dialog v-model="dialog" max-width="920" persistent scrollable>
        <v-card>
            <v-card-title class="d-flex align-center justify-space-between">
                <span class="text-h6">{{ tt('Consolidate Addresses', 'Consolidate Addresses') }}</span>
                <v-btn icon variant="text" @click="closeDialog">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>
            <v-divider />

            <!-- Step header -->
            <div class="d-flex align-center ga-2 px-4 py-3">
                <v-chip :color="step === 1 ? 'primary' : undefined" size="small" label>1. {{ tt('Select', 'Select') }}</v-chip>
                <v-icon size="16">mdi-chevron-right</v-icon>
                <v-chip :color="step === 2 ? 'primary' : undefined" size="small" label>2. {{ tt('Review', 'Review') }}</v-chip>
                <v-icon size="16">mdi-chevron-right</v-icon>
                <v-chip :color="step === 3 ? 'primary' : undefined" size="small" label>3. {{ tt('Confirm', 'Confirm') }}</v-chip>
            </div>
            <v-divider />

            <v-card-text style="max-height: 66vh;">
                <!-- STEP 1: Select two + keep -->
                <template v-if="step === 1">
                    <div v-if="isLoadingList" class="d-flex flex-column align-center justify-center py-10">
                        <v-progress-circular indeterminate color="primary" size="40" width="4" />
                        <div class="text-caption text-medium-emphasis mt-3">{{ tt('Loading...', 'Loading...') }}</div>
                    </div>
                    <template v-else>
                    <p class="text-body-2 text-medium-emphasis mb-3">
                        {{ tt('Select exactly two addresses to consolidate, then choose which one to keep. The other\'s role mappings will move to the kept address.', 'Select exactly two addresses to consolidate, then choose which one to keep. The other\'s role mappings will move to the kept address.') }}
                    </p>
                    <div v-if="addresses.length < 2" class="text-medium-emphasis pa-4 text-center">
                        {{ tt('Need at least two addresses to consolidate.', 'Need at least two addresses to consolidate.') }}
                    </div>
                    <v-list v-else lines="two" density="comfortable">
                        <v-list-item
                            v-for="addr in addresses"
                            :key="addr.id"
                            :class="{ 'bg-blue-lighten-5': isSelected(addr.id) }"
                            @click="toggleSelect(addr.id)"
                        >
                            <template #prepend>
                                <v-checkbox-btn
                                    :model-value="isSelected(addr.id)"
                                    :disabled="!isSelected(addr.id) && selectedIds.length >= 2"
                                    @click.stop="toggleSelect(addr.id)"
                                />
                            </template>
                            <v-list-item-title class="font-weight-medium text-truncate" :title="addr.displayLine">
                                {{ addr.displayLine || tt('(empty)', '(empty)') }}
                            </v-list-item-title>
                            <v-list-item-subtitle v-if="addr.alias">{{ tt('Alias', 'Alias') }}: {{ addr.alias }}</v-list-item-subtitle>
                            <template #append>
                                <div class="d-flex align-center ga-3 flex-shrink-0">
                                    <v-chip size="x-small" color="info" variant="tonal" class="flex-shrink-0">
                                        {{ addr.roleCount }} {{ tt('role(s)', 'role(s)') }}
                                    </v-chip>
                                    <v-radio-group
                                        :model-value="keepId"
                                        hide-details
                                        density="compact"
                                        @update:model-value="(v: any) => (keepId = v)"
                                    >
                                        <v-radio
                                            :value="addr.id"
                                            :disabled="!isSelected(addr.id)"
                                            color="success"
                                            @click.stop
                                        >
                                            <template #label><span class="text-caption">{{ tt('Keep', 'Keep') }}</span></template>
                                        </v-radio>
                                    </v-radio-group>
                                </div>
                            </template>
                        </v-list-item>
                    </v-list>
                    </template>
                </template>

                <!-- STEP 2: Review -->
                <template v-else-if="step === 2">
                    <v-progress-linear v-if="isLoadingPreview" indeterminate color="primary" class="mb-3" />
                    <div class="d-flex flex-column flex-md-row ga-3 mb-4">
                        <v-card variant="tonal" color="success" class="flex-1-1">
                            <v-card-text>
                                <div class="text-overline">{{ tt('KEEP', 'KEEP') }}</div>
                                <div class="font-weight-medium">{{ keepAddress?.displayLine || tt('(empty)', '(empty)') }}</div>
                                <div class="text-caption">{{ keepAddress?.addressTypeLabel }} · {{ keepAddress?.roleCount }} {{ tt('role(s)', 'role(s)') }}</div>
                            </v-card-text>
                        </v-card>
                        <v-icon class="align-self-center d-none d-md-flex">mdi-arrow-left-bold</v-icon>
                        <v-card variant="tonal" class="flex-1-1">
                            <v-card-text>
                                <div class="text-overline">{{ tt('MERGE', 'MERGE') }}</div>
                                <div class="font-weight-medium">{{ mergeAddress?.displayLine || tt('(empty)', '(empty)') }}</div>
                                <div class="text-caption">{{ mergeAddress?.addressTypeLabel }} · {{ mergeAddress?.roleCount }} {{ tt('role(s)', 'role(s)') }}</div>
                            </v-card-text>
                        </v-card>
                    </div>

                    <div class="text-subtitle-2 mb-2">
                        {{ tt('Role mappings to move', 'Role mappings to move') }} ({{ reassignCount }})
                        <span v-if="collisionCount > 0" class="text-caption text-warning">
                            · {{ collisionCount }} {{ tt('duplicate(s) will be dropped', 'duplicate(s) will be dropped') }}
                        </span>
                    </div>
                    <v-table density="compact" class="border rounded">
                        <thead>
                            <tr>
                                <th>{{ tt('Company', 'Company') }}</th>
                                <th>{{ tt('Position', 'Position') }}</th>
                                <th>{{ tt('Address Type', 'Address Type') }}</th>
                                <th>{{ tt('Action', 'Action') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-if="reassignments.length === 0">
                                <td colspan="4" class="text-center text-medium-emphasis">{{ tt('No role mappings on the merged address.', 'No role mappings on the merged address.') }}</td>
                            </tr>
                            <tr v-for="(r, i) in reassignments" :key="i">
                                <td>{{ r.companyName || r.entityCd || '-' }}</td>
                                <td>{{ r.position || r.positionCd || '-' }}</td>
                                <td>{{ r.addrTypeLabel }}</td>
                                <td>
                                    <v-chip v-if="r.action === 'drop'" size="x-small" color="warning" label>{{ tt('Drop (duplicate)', 'Drop (duplicate)') }}</v-chip>
                                    <v-chip v-else size="x-small" color="primary" label>{{ tt('Move to Keep', 'Move to Keep') }}</v-chip>
                                </td>
                            </tr>
                        </tbody>
                    </v-table>
                </template>

                <!-- STEP 3: Confirm -->
                <template v-else>
                    <p class="text-body-2 mb-3">
                        {{ reassignCount }} {{ tt('role mapping(s) will be moved to the kept address.', 'role mapping(s) will be moved to the kept address.') }}
                    </p>
                    <v-checkbox
                        v-model="deleteOrphan"
                        hide-details
                        color="error"
                        :label="tt('Also delete the merged address', 'Also delete the merged address')"
                    />
                    <v-alert v-if="deleteOrphan" type="warning" variant="tonal" density="compact" class="mt-2">
                        {{ tt('The merged address will be removed from the address list.', 'The merged address will be removed from the address list.') }}
                    </v-alert>
                    <v-progress-linear v-if="isSubmitting" indeterminate color="primary" class="mt-4" />
                </template>
            </v-card-text>

            <v-divider />
            <v-card-actions class="justify-end pa-3">
                <v-btn variant="text" @click="closeDialog">{{ tt('Cancel', 'Cancel') }}</v-btn>
                <v-spacer />
                <v-btn v-if="step > 1" variant="outlined" :disabled="isSubmitting" @click="step = (step - 1) as any">
                    {{ tt('Back', 'Back') }}
                </v-btn>
                <v-btn
                    v-if="step === 1"
                    color="primary"
                    variant="flat"
                    :loading="isLoadingPreview"
                    :disabled="!canReview"
                    @click="goToReview"
                >
                    {{ tt('Review', 'Review') }}
                </v-btn>
                <v-btn
                    v-else-if="step === 2"
                    color="primary"
                    variant="flat"
                    @click="goToConfirm"
                >
                    {{ tt('Next', 'Next') }}
                </v-btn>
                <v-btn
                    v-else
                    :color="deleteOrphan ? 'error' : 'primary'"
                    variant="flat"
                    :loading="isSubmitting"
                    @click="executeConsolidate"
                >
                    {{ deleteOrphan ? tt('Consolidate & Delete', 'Consolidate & Delete') : tt('Consolidate', 'Consolidate') }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>
